import { Button, message } from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
	getContractorOrWorkOrderId,
	getSanitisedWorkOrder,
	isAValidAndParitalWorkOrder,
} from '../../utils/WorkOrderUtils';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { SCHEDULE_TECH_STATUSES, TECH_SCHEDULED_STATUSES } from '../../constants/WorkOrderStatuses';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import ScheduleServiceCallModalForm from '../schedule_service_call_modal_form/ScheduleServiceCallModalForm';
import WorkOrderRowDisplay from '../work_order_row_display/WorkOrderRowDisplay';
import {
	rescheduleServiceCallForContractor,
	rescheduleServiceCallForSupplier,
	scheduleServiceCallForContractor,
	scheduleServiceCallForSupplier,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import { formatDateForLocationTimeZone } from '../../utils/DataFormatterUtils';

const ServiceCalendarWorkOrderRow = (props: any) => {
	const {
		record,
		currentUser,
		refreshTable = () => {},
		onRefreshTable = () => {},
		scheduleServiceCall,
		location,
		backText,
		targetCollectionName,
		actions,
		getFullWorkOrder,
		serviceScheduledAt,
		leadTechnicianEmail,
		rescheduleServiceCall,
	} = props;

	const [modal, setModal] = useState<string | null>(null);
	const [isLoadingFullWorkOrder, setIsLoadingFullWorkOrder] = useState(false);

	// Keep refs for modal forms
	const formRefs = useRef<any>({
		scheduleServiceCallForm: null,
		rescheduleServiceCallForm: null,
		manualCheckInForm: null,
		manualCheckOutForm: null,
	});

	const saveFormRef = useCallback(
		(formName: string) => (formRef: any) => {
			formRefs.current[formName] = formRef;
		},
		[]
	);

	const ensureFullWorkOrder = useCallback(
		async (workOrder: any) => {
			if (isAValidAndParitalWorkOrder(workOrder)) {
				if (isLoadingFullWorkOrder) {
					return workOrder;
				}
				setIsLoadingFullWorkOrder(true);
				try {
					const fullWorkOrder = await getFullWorkOrder(workOrder.id);
					return getSanitisedWorkOrder(fullWorkOrder, true);
				} catch (error) {
					message.error('Failed to load work order details. Please try again.');
					throw error;
				} finally {
					setIsLoadingFullWorkOrder(false);
				}
			}
			return workOrder;
		},
		[isLoadingFullWorkOrder, getFullWorkOrder]
	);

	const withFullWorkOrder = useCallback(
		(action: (fullWorkOrder: any, ...args: any[]) => void) => {
			return async (...args: any[]) => {
				const workOrder = getSanitisedWorkOrder(record, true);
				const fullWorkOrder = await ensureFullWorkOrder(workOrder);
				return action(fullWorkOrder, ...args);
			};
		},
		[record, ensureFullWorkOrder]
	);

	const BACK_TEXT = backText || 'Back to work orders';
	// const isPrivateSupplier = nullSafeGet('facility.status', currentUser) === 'private';
	const workOrder = getSanitisedWorkOrder(record, true);

	const getWorkOrderActions = useCallback(() => {
		if (workOrder === undefined) return [];
		if (actions) return actions;

		const schedule = (
			<Button
				key="schedule"
				onClick={withFullWorkOrder((fullWorkOrder: any, e: React.MouseEvent) => {
					stopDefaultEvents(e);
					setModal('scheduleServiceCallFormVisible');
				})}
				loading={isLoadingFullWorkOrder}
				className="w-full"
			>
				Schedule
			</Button>
		);

		const reschedule = (
			<Button
				className="w-full"
				key="reschedule"
				onClick={withFullWorkOrder((fullWorkOrder: any, e: React.MouseEvent) => {
					stopDefaultEvents(e);
					setModal('rescheduleServiceCallFormVisible');
				})}
				loading={isLoadingFullWorkOrder}
			>
				Reschedule
			</Button>
		);

		if (SCHEDULE_TECH_STATUSES.includes(workOrder.status)) {
			return [schedule];
		} else if (TECH_SCHEDULED_STATUSES.includes(workOrder.status)) {
			return [reschedule];
		} else {
			return [schedule];
		}
	}, [actions, workOrder, isLoadingFullWorkOrder, withFullWorkOrder]);

	const getLink = useCallback(() => {
		const path = `/supplier/workOrders/detail/${nullSafeGet('id', record)}/details${
			targetCollectionName ? `?targetCollectionName=${targetCollectionName}` : ''
		}`;
		return getLinkWIthBackLinkParams(location, BACK_TEXT, path, {
			preserve: true,
		});
	}, [record, location, BACK_TEXT, targetCollectionName]);

	return (
		<>
			{modal === 'scheduleServiceCallFormVisible' && (
				<ScheduleServiceCallModalForm
					formData={{
						workOrder,
						supplierFacilityId: nullSafeGet('facility.id', currentUser),
						serviceScheduledAt: serviceScheduledAt,
						leadTechnicianEmail: leadTechnicianEmail,
					}}
					wrappedComponentRef={saveFormRef('scheduleServiceCallForm')}
					visible={modal === 'scheduleServiceCallFormVisible'}
					onCancel={() => setModal(null)}
					onSubmit={() => {
						const form = nullSafeGet('current.scheduleServiceCallForm.props.form', formRefs);
						if (form) {
							form.validateFields((err: any, values: any) => {
								if (err) {
									return;
								}
								const timeZone =
									values['serviceScheduledAtTimeZone'] ||
									nullSafeGet('location.timeZoneId', workOrder);
								const formValues = {
									...values,
									serviceScheduledAt: formatDateForLocationTimeZone(
										values.serviceScheduledAt,
										timeZone
									),
								};
								scheduleServiceCall(workOrder, formValues).then(() => {
									setModal(null);
									const scheduledTime = formatDateForLocationTimeZone(
										values.serviceScheduledAt,
										timeZone
									);
									refreshTable(); // work order table refresh
									onRefreshTable(scheduledTime);
									message.success(`Service call scheduled.`);
								});
							});
						}
					}}
				/>
			)}
			{modal === 'rescheduleServiceCallFormVisible' && (
				<ScheduleServiceCallModalForm
					formData={{
						...nullSafeGetOrElse('lastServiceCall', workOrder, {}),
						leadTechnicianEmail: leadTechnicianEmail,
						workOrder,
					}}
					isReschedule={true}
					wrappedComponentRef={saveFormRef('rescheduleServiceCallForm')}
					visible={modal === 'rescheduleServiceCallFormVisible'}
					onCancel={() => setModal(null)}
					onSubmit={() => {
						const form = nullSafeGet('current.rescheduleServiceCallForm.props.form', formRefs);
						form.validateFields((err, values) => {
							if (err) {
								return;
							}
							const timeZone =
								values['serviceScheduledAtTimeZone'] ||
								nullSafeGet('location.timeZoneId', workOrder);
							const formValues = {
								...values,
								serviceScheduledAt: formatDateForLocationTimeZone(
									values.serviceScheduledAt,
									timeZone
								),
							};
							rescheduleServiceCall(workOrder, formValues).then(() => {
								setModal(null);

								const scheduledTime = formatDateForLocationTimeZone(
									values.serviceScheduledAt,
									timeZone
								);
								refreshTable(); // work order table refresh
								onRefreshTable(scheduledTime);
								message.success(`Service call rescheduled.`);
							});
						});
					}}
				/>
			)}
			<div className="flex flex-col flex-nowrap gap-y-4 p-4">
				<div className="flex-1 flex-grow ">
					<WorkOrderRowDisplay
						workOrder={record}
						showFullTitle
						titleActions={() => (
							<Link
								to={getLink()}
								style={{ padding: 0, marginLeft: 8, display: 'inline-block' }}
								target="_blank"
							>
								<i className="icons8-font icons8-external-link stroke-supplier text-sm text-supplier" />
							</Link>
						)}
					/>
				</div>

				<div className="flex flex-row items-start justify-end gap-y-2">{getWorkOrderActions()}</div>
			</div>
		</>
	);
};

const mapStateToProps = (state: any) => ({
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch: any) => ({
	getFullWorkOrder: (id: string) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	scheduleServiceCall: (workOrder: any, serviceCall: any) =>
		nullSafeGet('contractorWo', workOrder)
			? dispatch(
					scheduleServiceCallForContractor({
						...serviceCall,
						contractorWorkOrderId: getContractorOrWorkOrderId(workOrder),
						supplierFacilityId: nullSafeGet('contractorWo.subSupplierFacilityId', workOrder),
						workOrderId: undefined,
					})
			  )
			: dispatch(scheduleServiceCallForSupplier(serviceCall)),
	rescheduleServiceCall: (workOrder, serviceCall) =>
		nullSafeGet('contractorWo', workOrder)
			? dispatch(
					rescheduleServiceCallForContractor({
						...serviceCall,
						contractorWorkOrderId: getContractorOrWorkOrderId(workOrder),
						supplierFacilityId: nullSafeGet('contractorWo.subSupplierFacilityId', workOrder),
						workOrderId: undefined,
					})
			  )
			: dispatch(rescheduleServiceCallForSupplier(serviceCall)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ServiceCalendarWorkOrderRow);
